import { useEffect, useMemo } from "react";

import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";

import {
  createPsePayerSchema,
  PSE_DOCUMENT_TYPES,
  type PseDocumentType,
  type PsePayerValues,
  reconcilePseDocumentType,
} from "./psePayer.schema";

/** Values a re-entered step restores. Everything is optional: a first visit starts from the defaults. */
export type PsePayerDefaults = Partial<PsePayerValues>;

function asDocumentType(value: string | undefined): PseDocumentType {
  return (PSE_DOCUMENT_TYPES as readonly string[]).includes(value ?? "") ? (value as PseDocumentType) : "CC";
}

/**
 * The PSE payer form, wired once for both entry points.
 *
 * Owns the schema (translated here, validated in `psePayer.schema.ts`) and the one rule that cannot live in a
 * static schema: the person type drives the document type, so switching to a legal entity locks NIT and
 * switching back to a natural person releases it. Keeping that here means neither screen can forget it.
 */
export function usePsePayerForm(defaults: PsePayerDefaults) {
  const { t } = useTranslations("payins");
  const { t: tGlobal } = useTranslations("global");

  const schema = useMemo(
    () =>
      createPsePayerSchema({
        required: tGlobal("input_error_required"),
        documentInvalid: t("pse_document_invalid"),
        emailInvalid: t("pse_email_invalid"),
        phoneInvalid: t("pse_phone_invalid"),
        maxLength: (max) => t("pse_max_length", { max: String(max) }),
      }),
    [t, tGlobal],
  );

  const personTypeDefault = defaults.personType ?? "natural";
  const form = useZodForm(schema, {
    personType: personTypeDefault,
    documentType: reconcilePseDocumentType(personTypeDefault, asDocumentType(defaults.documentType)),
    documentNumber: defaults.documentNumber ?? "",
    payerName: defaults.payerName ?? "",
    email: defaults.email ?? "",
    phone: defaults.phone ?? "",
    address: defaults.address ?? "",
    bankCode: defaults.bankCode ?? "",
  });

  const personType = form.watch("personType");
  useEffect(() => {
    const current = form.getValues("documentType");
    const next = reconcilePseDocumentType(personType, current);
    if (next !== current) form.setValue("documentType", next, { shouldValidate: true });
    // `form` is the stable instance React Hook Form memoizes for the component's lifetime.
  }, [personType, form]);

  return form;
}
